import { Danger } from "iconsax-reactjs";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useDevices from "@/hooks/useDevices";
import { Select } from "@/ui";

const labels = {
  audioinput: {
    audioLevelText: 'input_level',
    headerText: 'microphone',
  },
  audiooutput: {
    audioLevelText: 'output_level',
    headerText: 'speaker',
  },
};

interface IProps {
  disabled: boolean;
  kind: 'audioinput' | 'audiooutput';
  onDeviceChange: (value: string) => void;
  setDeviceError: (value: string) => void;
  error?: string;
}

const AudioDevice: FC<IProps> = ({
  disabled,
  kind,
  onDeviceChange,
  setDeviceError,
  error,
}) => {
  const { t } = useTranslation();

  const devices = useDevices();
  const audioDevices = kind === 'audiooutput' ? devices.audioOutputDevices : devices.audioInputDevices;
  const [selectedDevice, setSelectedDevice] = useState('');
  const { headerText } = labels[kind];
  const noAudioRedirect = !Audio.prototype.setSinkId && kind === 'audiooutput';

  const updateSelectedDevice = useCallback(
    (value: string) => {
      onDeviceChange(value);
      setSelectedDevice(value);
      setDeviceError('');
    },
    [onDeviceChange, setSelectedDevice, setDeviceError]
  );

  useEffect(() => {
    const hasSelectedDevice = audioDevices.some((device) => device.deviceId === selectedDevice);
    if (audioDevices.length && !hasSelectedDevice) {
      updateSelectedDevice(audioDevices[0].deviceId);
    }
  }, [audioDevices, devices, selectedDevice, updateSelectedDevice]);

  return (
    <div className="diagnostics-audio-device">
      {noAudioRedirect ? (
        <Select
          disabled
          value="1"
          options={[{ label: t('system_default_audio_output'), value: '1' }]}
          onChange={() => {}}
        />
      ) : (
        <Select
          label={t(headerText)}
          value={selectedDevice}
          onChange={updateSelectedDevice}
          options={audioDevices.map((device) => ({ label: device.label, value: device.deviceId }))}
          disabled={disabled}
        />
      )}
      {!!error && (
        <p>
          <Danger
            size="18"
            color="var(--color-danger)"
            variant="Bold"
          />
          {error}
        </p>
      )}
    </div>
  );
};

export default AudioDevice;